import _ from "lodash";
import { sanitizeErrorMessage } from "@/app/schedules/[tn]/ErrorMessageSanitization";
import { CloneParams } from "@/app/schedules/[tn]/utils";
import {
    AutoAttendantStatusEnum,
    CallDeflectionFwdToTypeEnum,
    CallForwardingVariableStatusEnum,
    CommunicationSettings,
    SelectiveCallForwardingStatusEnum,
    SequentialRingStatusEnum,
    SimultaneousRingStatusEnum,
} from "@/clients/ntas/basic-selfcare";
import {
    OneTime,
    PredefinedSettingsIdList,
    PredefinedSettingsProfile,
    PredefinedSettingsSchedule,
    RecurrenceDayListsEnum,
    RecurrenceDaySelectionEnum,
    SettingsSchedule,
    SettingsScheduleStatus,
    TimeOfDay,
} from "@/clients/ntas/profiles";
import { makeTime } from "@/utils/general";
import { LoggerObject, sanitizeLogMessage } from "@/utils/logger";

export enum DayListTimesTypeEnum {
    ALL_DAY = "allDay",
    SPECIFIC_HOURS = "specificHours",
}

export enum DayListTypes {
    EMPTY = "empty",
    DEFAULT = "default",
    AFTER_HOURS = "after_hours",
    WEEKEND = "weekend",
    BUSINESS_HOURS = "business_hours",
    ALL_TIME = "all_time",
}

export interface DayListTimeItem {
    dayName: string;
    status: boolean;
    type: DayListTimesTypeEnum;
    timeSlots: {
        startTime: TimeOfDay;
        endTime: TimeOfDay;
        hasError: boolean;
    }[];
}

export const RecurringType = {
    AfterHours: "After hours",
    Weekends: "Weekends",
    BusinessHours: "Business hours",
    AllTime: "24/7",
    Custom: "Custom",
};

export const EmptyDayList: DayListTimeItem[] = [
    {
        dayName: RecurrenceDayListsEnum.Sunday,
        status: false,
        type: DayListTimesTypeEnum.ALL_DAY,
        timeSlots: [],
    },
    {
        dayName: RecurrenceDayListsEnum.Monday,
        status: false,
        type: DayListTimesTypeEnum.ALL_DAY,
        timeSlots: [],
    },
    {
        dayName: RecurrenceDayListsEnum.Tuesday,
        status: false,
        type: DayListTimesTypeEnum.ALL_DAY,
        timeSlots: [],
    },
    {
        dayName: RecurrenceDayListsEnum.Wednesday,
        status: false,
        type: DayListTimesTypeEnum.ALL_DAY,
        timeSlots: [],
    },
    {
        dayName: RecurrenceDayListsEnum.Thursday,
        status: false,
        type: DayListTimesTypeEnum.ALL_DAY,
        timeSlots: [],
    },
    {
        dayName: RecurrenceDayListsEnum.Friday,
        status: false,
        type: DayListTimesTypeEnum.ALL_DAY,
        timeSlots: [],
    },
    {
        dayName: RecurrenceDayListsEnum.Saturday,
        status: false,
        type: DayListTimesTypeEnum.ALL_DAY,
        timeSlots: [],
    },
];

export const FirstHalfStart = { hour: 0, minute: 0 };
export const FirstHalfEnd = { hour: 12, minute: 0 };
export const SecondHalfStart = { hour: 12, minute: 0 };
export const SecondHalfEnd = { hour: 0, minute: 0 };
export const BusinessHoursStart = { hour: 9, minute: 0 };
export const BusinessHoursEnd = { hour: 17, minute: 0 };

export const disableVoiceSettingsContinue = (commSettings: CommunicationSettings) => {
    if (commSettings.autoAttendant?.status === AutoAttendantStatusEnum.On) return !commSettings.autoAttendant?.rootVxmlFileId;
    if (commSettings?.callForwardingVariable?.status === CallForwardingVariableStatusEnum.On) {
        if (!commSettings?.callForwardingVariable.fwdToType) return true;
        else if (
            commSettings?.callForwardingVariable.fwdToType === CallDeflectionFwdToTypeEnum.Dn &&
            (!commSettings?.callForwardingVariable.fwdDN || commSettings?.callForwardingVariable.fwdDN.length !== 11)
        )
            return true;
    }
    if (commSettings?.selectiveCallForwarding?.status === SelectiveCallForwardingStatusEnum.On) {
        if (!commSettings.selectiveCallForwarding.fwdToType || !commSettings.selectiveCallForwarding.scfType || !commSettings.selectiveCallForwarding.scfList)
            return true;
        else if (
            commSettings.selectiveCallForwarding.fwdToType === CallDeflectionFwdToTypeEnum.Dn &&
            (!commSettings.selectiveCallForwarding.fwdDN || commSettings.selectiveCallForwarding.fwdDN.length !== 11)
        )
            return true;
    }
    if (
        commSettings?.simultaneousRing?.status === SimultaneousRingStatusEnum.On &&
        (!commSettings.simultaneousRing.simrngList ||
            commSettings.simultaneousRing.simrngList.length === 0 ||
            commSettings.simultaneousRing.simrngList.some((itm) => itm.simDN?.length !== 11))
    )
        return true;
    if (
        commSettings?.sequentialRing?.status === SequentialRingStatusEnum.On &&
        (!commSettings.sequentialRing.seqrngList ||
            commSettings.sequentialRing.seqrngList.length === 0 ||
            commSettings.sequentialRing.seqrngList.some((itm) => itm.seqDN?.length !== 11))
    )
        return true;

    return false;
};

/**
 * Map of profileId -> profileName, used to name the schedule that a conflict is against.
 */
export type ProfileNamesById = Record<string, string>;

/**
 * Builds the user facing schedule conflict message. When the conflicting schedule can be resolved
 * to a profile name we name it explicitly, otherwise we fall back to the generic wording.
 * @param conflictingProfileIds - profileIds of the schedules that were found to overlap
 * @param profileNames - map of profileId -> profileName
 */
export const buildScheduleConflictMessage = (conflictingProfileIds: string[], profileNames?: ProfileNamesById): string => {
    const names = conflictingProfileIds.map((id) => profileNames?.[id]).filter((name): name is string => Boolean(name));
    if (names.length === 0) return "Provided time overlaps with existing schedule, please select different time.";
    const quoted = names.map((name) => `"${name}"`);
    const list = quoted.length === 1 ? quoted[0] : `${quoted.slice(0, -1).join(", ")} and ${quoted[quoted.length - 1]}`;
    return `Provided time overlaps with the ${list} schedule${names.length > 1 ? "s" : ""}, please select different time.`;
};

/**
 * Finds the existing one time schedule entries that the user's entry overlaps with
 * @param oneTimeData - this contains the data user has entered on screen
 * @param schedule - this is the response from api, which will have all the schedules for the TN
 */
const findOneTimeOverlapEntries = (oneTimeData: OneTime, schedule: PredefinedSettingsSchedule): SettingsSchedule[] => {
    const oneTimeEntries = schedule.settingsScheduleEntry?.filter((sch) => sch.oneTime) || [];
    const startDate = new Date(oneTimeData.startDate as string);
    const endDate = new Date(oneTimeData.endDate as string);
    return oneTimeEntries.filter((entry: SettingsSchedule) => {
        const entryStart = new Date(entry.oneTime?.startDate as string);
        const entryEnd = new Date(entry.oneTime?.endDate as string);
        // Two ranges overlap unless one ends before the other starts. Testing only whether either end
        // of the user's range falls inside the entry would miss a range that fully contains it
        // (user 9-12 against an existing 10-11). Boundaries stay inclusive, so ranges that merely
        // touch still count as a conflict.
        return startDate <= entryEnd && endDate >= entryStart;
    });
};

/**
 * @returns the profileIds of every overlapping one time schedule, de-duplicated. Entries without a
 * profileId still count as an overlap (see oneTimeDatesOverlap) but cannot be named.
 */
export const findOneTimeOverlapProfileIds = (oneTimeData: OneTime, schedule: PredefinedSettingsSchedule): string[] => [
    ...new Set(
        findOneTimeOverlapEntries(oneTimeData, schedule)
            .map((entry) => entry.profileId as string)
            .filter(Boolean),
    ),
];

/**
 * Checks for overlaps with existing one time schedules
 * @param oneTimeData - this contains the data user has entered on screen
 * @param schedule - this is the response from api, which will have all the schedules for the TN
 * @returns true if the time & date entered by user overlaps with any of the existing one time date & time
 */
export const oneTimeDatesOverlap = (oneTimeData: OneTime, schedule: PredefinedSettingsSchedule): boolean =>
    findOneTimeOverlapEntries(oneTimeData, schedule).length > 0;

export const checkRecurrenceOverlap = (daysList: DayListTimeItem[], entryStartTime: Date, entryEndTime: Date): boolean => {
    return daysList
        .filter((day) => day.status)
        .some((day) => {
            // TODO: revist the logic, has too many if cases - got review comment from chris
            if (day.type === DayListTimesTypeEnum.ALL_DAY) {
                const dayStartTime = makeTime(FirstHalfStart.hour, FirstHalfStart.minute);
                const dayEndTime = makeTime(FirstHalfEnd.hour, FirstHalfEnd.minute);
                // we need this because if end time is 12:00AM and when we create a date it is acting like start of the day, instead it should be considered as end of the current day or start of next day, hence setting this to 24
                if (dayEndTime.getHours() === 0) dayEndTime.setHours(24);
                // this statement checks if the entry overlaps with the first half of the day
                if (
                    (dayStartTime > entryStartTime && dayStartTime < entryEndTime) ||
                    (dayEndTime > entryStartTime && dayEndTime < entryEndTime) ||
                    (entryStartTime > dayStartTime && entryStartTime < dayEndTime) ||
                    (entryEndTime > dayStartTime && entryEndTime < dayEndTime)
                ) {
                    return true;
                }
                const secondHalfStartTime = makeTime(SecondHalfStart.hour, SecondHalfStart.minute);
                const secondHalfEndTime = makeTime(SecondHalfEnd.hour, SecondHalfEnd.minute);
                // we need this because if end time is 12:00AM and when we create a date it is acting like start of the day, instead it should be considered as end of the current day or start of next day, hence setting this to 24
                if (secondHalfEndTime.getHours() === 0) secondHalfEndTime.setHours(24);
                // this statement checks if the entry overlaps with the second half of the day
                return (
                    (secondHalfStartTime > entryStartTime && secondHalfStartTime < entryEndTime) ||
                    (secondHalfEndTime > entryStartTime && secondHalfEndTime < entryEndTime) ||
                    (entryStartTime > secondHalfStartTime && entryStartTime < secondHalfEndTime) ||
                    (entryEndTime > secondHalfStartTime && entryEndTime < secondHalfEndTime)
                );
            }

            return day.timeSlots.some((slot) => {
                if (!timeHasValues(slot.startTime) || !timeHasValues(slot.endTime)) return false;
                const dayStartTime = makeTime(slot.startTime.hour as number, slot.startTime.minute as number);
                const dayEndTime = makeTime(slot.endTime.hour as number, slot.endTime.minute as number);
                // we need this because if end time is 12:00AM and when we create a date it is acting like start of the day, instead it should be considered as end of the current day or start of next day, hence setting this to 24
                if (dayEndTime.getHours() === 0) dayEndTime.setHours(24);
                // this statement checks if the entry overlaps with the timings entered by the user
                return (
                    (dayStartTime > entryStartTime && dayStartTime < entryEndTime) ||
                    (dayEndTime > entryStartTime && dayEndTime < entryEndTime) ||
                    (entryStartTime > dayStartTime && entryStartTime < dayEndTime) ||
                    (entryEndTime > dayStartTime && entryEndTime < dayEndTime)
                );
            });
        });
};

/**
 * Finds the existing Recurring Schedule entries that the user's selection overlaps with
 * @param dayListTimes - this contains the each day status and time slots for each day selcted by user in UI
 * @param schedule - this is the response from api, which will have all the schedules for the TN
 */
const findRecurringOverlapEntries = (dayListTimes: DayListTimeItem[], schedule: PredefinedSettingsSchedule): SettingsSchedule[] => {
    const allEntries = schedule.settingsScheduleEntry?.filter((sch) => sch.recurring) || [];
    return allEntries.filter((schd) => {
        const entryStartTime = makeTime(schd.recurring?.startTime?.hour as number, schd.recurring?.startTime?.minute as number);
        const entryEndTime = makeTime(schd.recurring?.endTime?.hour as number, schd.recurring?.endTime?.minute as number);
        // we need this because if end time is 12:00AM and when we create a date it is acting like start of the day, instead it should be considered as end of the current day or start of next day, hence setting this to 24
        if (entryEndTime.getHours() === 0) entryEndTime.setHours(24);

        switch (schd.recurring?.daySelection) {
            case RecurrenceDaySelectionEnum.Daily:
                return checkRecurrenceOverlap(dayListTimes, entryStartTime, entryEndTime);
            case RecurrenceDaySelectionEnum.Weekday: {
                const filteredDays = dayListTimes.filter((day) =>
                    [
                        RecurrenceDayListsEnum.Monday as string,
                        RecurrenceDayListsEnum.Tuesday as string,
                        RecurrenceDayListsEnum.Wednesday as string,
                        RecurrenceDayListsEnum.Thursday as string,
                        RecurrenceDayListsEnum.Friday as string,
                    ].includes(day.dayName),
                );
                return checkRecurrenceOverlap(filteredDays, entryStartTime, entryEndTime);
            }
            case RecurrenceDaySelectionEnum.Weekend: {
                const filteredDays = dayListTimes.filter((day) =>
                    [RecurrenceDayListsEnum.Sunday as string, RecurrenceDayListsEnum.Saturday as string].includes(day.dayName),
                );
                return checkRecurrenceOverlap(filteredDays, entryStartTime, entryEndTime);
            }
            case RecurrenceDaySelectionEnum.SpecificDays: {
                const filteredDays = dayListTimes.filter((day) => schd.recurring?.dayList?.includes(day.dayName as RecurrenceDayListsEnum));
                return checkRecurrenceOverlap(filteredDays, entryStartTime, entryEndTime);
            }
            default:
                return false;
        }
    });
};

/**
 * @returns the profileIds of every overlapping recurring schedule, de-duplicated. Entries without a
 * profileId still count as an overlap (see recurringDatesOverlap) but cannot be named.
 */
export const findRecurringOverlapProfileIds = (dayListTimes: DayListTimeItem[], schedule: PredefinedSettingsSchedule): string[] => [
    ...new Set(
        findRecurringOverlapEntries(dayListTimes, schedule)
            .map((schd) => schd.profileId as string)
            .filter(Boolean),
    ),
];

/**
 * Checks for overlaps with existing Recurring Schedules
 * @param dayListTimes - this contains the each day status and time slots for each day selcted by user in UI
 * @param schedule - this is the response from api, which will have all the schedules for the TN
 * @returns true if there is an overlap with existing Recurring Schedules, else false
 */
export const recurringDatesOverlap = (dayListTimes: DayListTimeItem[], schedule: PredefinedSettingsSchedule): boolean =>
    findRecurringOverlapEntries(dayListTimes, schedule).length > 0;

/**
 * @param oneTimeData - the one time dates entered by the user
 * @param schedule - existing schedules for the TN, when provided the returned errors also report
 *                   which existing schedule the entered dates conflict with
 * @param profileNames - map of profileId -> profileName, used to name the conflicting schedule
 */
export const getOneTimeScheduleErrors = (oneTimeData: OneTime, schedule?: PredefinedSettingsSchedule, profileNames?: ProfileNamesById): string[] => {
    const errors: string[] = [];
    if (!oneTimeData.startDate || !oneTimeData.endDate) return [];
    const startDate = new Date(oneTimeData.startDate);
    const endDate = new Date(oneTimeData.endDate);
    const now = new Date();
    if (startDate > endDate) errors.push("End date should be greater than start date & time");
    if (startDate < now) errors.push("Start date should be greater than current date & time");
    if (endDate < now) errors.push("End date should be greater than current date & time");
    if (schedule && oneTimeDatesOverlap(oneTimeData, schedule)) {
        errors.push(buildScheduleConflictMessage(findOneTimeOverlapProfileIds(oneTimeData, schedule), profileNames));
    }
    return errors;
};

export const timeHasValues = (time: TimeOfDay) => {
    return time && time.hour !== undefined && time.minute !== undefined && time.hour >= 0 && time.minute >= 0;
};

export const getRecurringScheduleErrors = (dayItem: DayListTimeItem, schedule: PredefinedSettingsSchedule, profileNames?: ProfileNamesById): string[] => {
    const errors: string[] = [];

    const pushError = (msg: string) => {
        if (!errors.some((error) => error === msg)) errors.push(msg);
    };

    if (recurringDatesOverlap([dayItem], schedule)) pushError(buildScheduleConflictMessage(findRecurringOverlapProfileIds([dayItem], schedule), profileNames));
    if (dayItem.type === DayListTimesTypeEnum.SPECIFIC_HOURS) {
        dayItem.timeSlots.forEach((slot, index) => {
            if (timeHasValues(slot.startTime) && timeHasValues(slot.endTime)) {
                if (
                    (!(slot.endTime.hour === 0 && slot.endTime.minute === 0) && (slot.startTime.hour as number) > (slot.endTime.hour as number)) ||
                    (slot.startTime.hour === slot.endTime.hour && (slot.startTime.minute as number) >= (slot.endTime.minute as number))
                ) {
                    pushError(`End time should be greater than start time and less than 12:00 AM (Midnight)`);
                }

                // Check for duplicate timeslots
                const duplicates = dayItem.timeSlots.filter(
                    (otherSlot, otherIndex) =>
                        otherIndex !== index &&
                        otherSlot.startTime.hour === slot.startTime.hour &&
                        otherSlot.startTime.minute === slot.startTime.minute &&
                        otherSlot.endTime.hour === slot.endTime.hour &&
                        otherSlot.endTime.minute === slot.endTime.minute,
                );
                if (duplicates.length > 0) {
                    pushError("Duplicate timeslots are not allowed");
                }
            }
        });
    }
    return errors;
};

export const getRecurringTimeSlotErrors = (dayItem: DayListTimeItem, schedule: PredefinedSettingsSchedule): boolean[] => {
    if (dayItem.type !== DayListTimesTypeEnum.SPECIFIC_HOURS) return dayItem.timeSlots.map(() => false);

    return dayItem.timeSlots.map((slot, index) => {
        if (!timeHasValues(slot.startTime) || !timeHasValues(slot.endTime)) return false;

        const invalidRange =
            (!(slot.endTime.hour === 0 && slot.endTime.minute === 0) && (slot.startTime.hour as number) > (slot.endTime.hour as number)) ||
            (slot.startTime.hour === slot.endTime.hour && (slot.startTime.minute as number) >= (slot.endTime.minute as number));

        const duplicate = dayItem.timeSlots.some(
            (otherSlot, otherIndex) =>
                otherIndex !== index &&
                otherSlot.startTime.hour === slot.startTime.hour &&
                otherSlot.startTime.minute === slot.startTime.minute &&
                otherSlot.endTime.hour === slot.endTime.hour &&
                otherSlot.endTime.minute === slot.endTime.minute,
        );

        const overlap = recurringDatesOverlap(
            [
                {
                    ...dayItem,
                    type: DayListTimesTypeEnum.SPECIFIC_HOURS,
                    timeSlots: [slot],
                },
            ],
            schedule,
        );

        return invalidRange || duplicate || overlap;
    });
};

export const getRecurringScheduleValidation = (dayItem: DayListTimeItem, schedule: PredefinedSettingsSchedule, profileNames?: ProfileNamesById) => {
    return {
        errors: getRecurringScheduleErrors(dayItem, schedule, profileNames),
        timeSlotErrors: getRecurringTimeSlotErrors(dayItem, schedule),
    };
};

interface PopulateRecurranceValuesInterface {
    profileId: string;
    schedule: PredefinedSettingsSchedule;
    setRecurringTypeRadio: (val: string) => void;
    setDayListTimes: (value: DayListTimeItem[]) => void;
    WeekendDateTimes: DayListTimeItem[];
    AllTimeDateTimes: DayListTimeItem[];
    BusinessHoursDateTimes: DayListTimeItem[];
    AfterHoursDateTimes: DayListTimeItem[];
    AllDayTimings: { startTime: TimeOfDay; endTime: TimeOfDay }[];
}

/**
 * this methods is used in case of edit, duplicate schedule flow.
 * It will help populate the values in UI from the selected profile/schedule in the wizard flow
 */
export const populateRecurranceValues = ({
    profileId,
    schedule,
    setRecurringTypeRadio,
    setDayListTimes,
    WeekendDateTimes,
    AllTimeDateTimes,
    BusinessHoursDateTimes,
    AfterHoursDateTimes,
    AllDayTimings,
}: PopulateRecurranceValuesInterface) => {
    const allRecurringSchedules = schedule.settingsScheduleEntry?.filter((entry) => entry.profileId === profileId && entry.recurring) || [];
    // For Business Hours - there will be exactly 1 schedule
    if (allRecurringSchedules.length === 1 && allRecurringSchedules[0].recurring?.daySelection === RecurrenceDaySelectionEnum.Weekday) {
        // setRecurringTypeRadio(RecurringType.BusinessHours);
        setRecurringTypeRadio(RecurringType.Custom);
        const dayList = _.cloneDeep(BusinessHoursDateTimes);
        dayList.forEach((itm) => {
            if (itm.status) {
                itm.timeSlots[0].startTime = allRecurringSchedules[0].recurring?.startTime as TimeOfDay;
                itm.timeSlots[0].endTime = allRecurringSchedules[0].recurring?.endTime as TimeOfDay;
            }
        });
        setDayListTimes(dayList);
    }
    // For 24/7 - there will be exactly 2 schedules(one for first half day, other for second half day)
    else if (allRecurringSchedules.length === 2 && allRecurringSchedules[0].recurring?.daySelection === RecurrenceDaySelectionEnum.Daily) {
        // setRecurringTypeRadio(RecurringType.AllTime);
        setRecurringTypeRadio(RecurringType.Custom);
        setDayListTimes(AllTimeDateTimes);
    }
    // For After Hours - there will be exactly 2 schedules(one for mrng hrs, other for evening hrs)
    else if (allRecurringSchedules.length === 2 && allRecurringSchedules[0].recurring?.daySelection === RecurrenceDaySelectionEnum.Weekday) {
        // setRecurringTypeRadio(RecurringType.AfterHours);
        setRecurringTypeRadio(RecurringType.Custom);
        const dayList = _.cloneDeep(AfterHoursDateTimes);
        dayList.forEach((itm) => {
            if (itm.status) {
                itm.timeSlots[0].startTime = allRecurringSchedules[0].recurring?.startTime as TimeOfDay;
                itm.timeSlots[0].endTime = allRecurringSchedules[0].recurring?.endTime as TimeOfDay;
                itm.timeSlots[1].startTime = allRecurringSchedules[1].recurring?.startTime as TimeOfDay;
                itm.timeSlots[1].endTime = allRecurringSchedules[1].recurring?.endTime as TimeOfDay;
            }
        });
        setDayListTimes(dayList);
    }
    // For weekends - all the schedules should be of type Weekend
    else if (allRecurringSchedules.length !== 0 && allRecurringSchedules.every((sch) => sch.recurring?.daySelection === RecurrenceDaySelectionEnum.Weekend)) {
        // setRecurringTypeRadio(RecurringType.Weekends);
        setRecurringTypeRadio(RecurringType.Custom);
        const dayList = _.cloneDeep(WeekendDateTimes);
        if (
            !(
                allRecurringSchedules.length === 2 &&
                allRecurringSchedules.some(
                    (sch) => _.isEqual(sch.recurring?.startTime, AllDayTimings[0].startTime) && _.isEqual(sch.recurring?.endTime, AllDayTimings[0].endTime),
                ) &&
                allRecurringSchedules.some(
                    (sch) => _.isEqual(sch.recurring?.startTime, AllDayTimings[1].startTime) && _.isEqual(sch.recurring?.endTime, AllDayTimings[1].endTime),
                )
            )
        ) {
            const sat = dayList.find((itm) => itm.dayName === RecurrenceDayListsEnum.Saturday) as DayListTimeItem;
            sat.type = DayListTimesTypeEnum.SPECIFIC_HOURS;
            sat.timeSlots = allRecurringSchedules.map((sch) => ({
                startTime: sch.recurring?.startTime as TimeOfDay,
                endTime: sch.recurring?.endTime as TimeOfDay,
                hasError: false,
            }));
            const sun = dayList.find((itm) => itm.dayName === RecurrenceDayListsEnum.Sunday) as DayListTimeItem;
            sun.type = DayListTimesTypeEnum.SPECIFIC_HOURS;
            sun.timeSlots = allRecurringSchedules.map((sch) => ({
                startTime: sch.recurring?.startTime as TimeOfDay,
                endTime: sch.recurring?.endTime as TimeOfDay,
                hasError: false,
            }));
        }
        setDayListTimes(dayList);
    } else {
        setRecurringTypeRadio(RecurringType.Custom);
        const dayList: DayListTimeItem[] = _.cloneDeep(EmptyDayList);
        const specificSchedules = allRecurringSchedules.filter(
            (sch: SettingsSchedule) => sch.recurring?.daySelection === RecurrenceDaySelectionEnum.SpecificDays,
        );

        // this will group the schedules with same dayList
        const groupedByDayList = specificSchedules.reduce((groups, item) => {
            const group = groups.find((group) => group.length > 0 && _.isEqual(group[0].recurring?.dayList, item.recurring?.dayList));
            if (group) {
                group.push(item);
            } else {
                groups.push([item]);
            }
            return groups;
        }, [] as SettingsSchedule[][]);

        groupedByDayList.forEach((group) => {
            // this condition checks if the selection is AllDay selection
            if (
                group.length === 2 &&
                group.some(
                    (sch) => _.isEqual(sch.recurring?.startTime, AllDayTimings[0].startTime) && _.isEqual(sch.recurring?.endTime, AllDayTimings[0].endTime),
                ) &&
                group.some(
                    (sch) => _.isEqual(sch.recurring?.startTime, AllDayTimings[1].startTime) && _.isEqual(sch.recurring?.endTime, AllDayTimings[1].endTime),
                )
            ) {
                group.forEach((sch) => {
                    sch.recurring?.dayList?.forEach((day) => {
                        const entry = dayList.find((itm) => itm.dayName === day);
                        if (entry) {
                            entry.status = true;
                            entry.type = DayListTimesTypeEnum.ALL_DAY;
                        }
                    });
                });
            } else {
                group.forEach((sch) => {
                    sch.recurring?.dayList?.forEach((day) => {
                        const entry = dayList.find((itm) => itm.dayName === day);
                        if (entry) {
                            entry.status = true;
                            entry.type = DayListTimesTypeEnum.SPECIFIC_HOURS;
                            entry.timeSlots.push({
                                startTime: sch.recurring?.startTime as TimeOfDay,
                                endTime: sch.recurring?.endTime as TimeOfDay,
                                hasError: false,
                            });
                        }
                    });
                });
            }
        });
        dayList.forEach(
            (itm) =>
                itm.timeSlots.length === 0 &&
                itm.timeSlots.push({
                    startTime: {},
                    endTime: {},
                    hasError: false,
                }),
        );
        setDayListTimes(dayList);
    }
};

/**
 * This function is used to create a new profile for the given phone number based off of the current comm settings.
 * @param tn - The phone number to create the profile for.
 * @param profileName - The name of the profile to be created.
 */
export async function createProfile(tn: string, profileName: string, parentLoggerObj: LoggerObject): Promise<void> {
    const loggerObj = parentLoggerObj.createChildLogger("createProfile");
    loggerObj.getLogger().debug("Entered Function");

    const res: Response = await fetch(`/api/v1/tns/${tn}/profiles/create`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({ profileName }),
    });
    if (!res.ok) {
        const err: string = await res.text();
        throw new Error(sanitizeErrorMessage(err), { cause: res.status });
    }
}

/**
 * This function is used to clone a profile for the given phone number.
 * @param tn - The phone number to clone the profile for.
 * @param params - The parameters to clone the profile, such as the profile ID to clone and the new profile name.
 */
export async function cloneProfile(tn: string, params: CloneParams, parentLoggerObj: LoggerObject): Promise<void> {
    const loggerObj = parentLoggerObj.createChildLogger("cloneProfile");
    loggerObj.getLogger().debug("Entered Function");

    const res: Response = await fetch(`/api/v1/tns/${tn}/profiles/clone`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({ ...params }),
    });
    if (!res.ok) {
        const err: string = await res.text();
        loggerObj.getLogger().debug(JSON.stringify(sanitizeLogMessage(err)));
        throw new Error(sanitizeErrorMessage(err), { cause: res.status });
    }
}

/**
 * This function is used to get the profile for the given phone number and profile ID.
 * @param tn - The phone number to get the profile for.
 * @param profileId - The ID of the profile to get.
 * @returns The profile for the given phone number and profile ID.
 */
export async function getProfile(
    tn: string,
    profileId: string,
    parentLoggerObj: LoggerObject,
): Promise<{ predefinedSettingsProfile: PredefinedSettingsProfile }> {
    const loggerObj = parentLoggerObj.createChildLogger("getProfile");
    loggerObj.getLogger().debug("Entered Function");

    const res: Response = await fetch(`/api/v1/tns/${tn}/profiles/${profileId}`);
    if (!res.ok) {
        const err: string = await res.text();
        loggerObj.getLogger().debug(JSON.stringify(sanitizeLogMessage(err)));
        throw new Error(sanitizeErrorMessage(err), { cause: res.status });
    }
    return res.json();
}

/**
 * This function is used to perform the First Time User Experience (FTUE) for profiles.
 * FTUE will be performed when no profiles are found for the given phone number.
 * FTUE should include creating two profiles: "Default" and "Away".
 * FTUE should also include setting the "Default" profile as the active profile.
 * FTUE should also include creating a schedule for the "Default" profile.
 * @param tn - The phone number to perform the FTUE for.
 */
export async function profilesFTUE(tn: string): Promise<void> {
    const loggerObj = new LoggerObject("profilesFTUE", undefined);
    loggerObj.getLogger().info("No profiles found for TN: " + tn + ". Performing FTUE.");
    // TODO: FTUE will be developed here.
}

/**
 * This function is used to get the list of profiles for the given phone number.
 * @param tn - The phone number to get the profiles for.
 * @returns The list of profiles for the given phone number.
 */
export async function getProfiles(tn: string, parentLoggerObj: LoggerObject): Promise<{ predefinedSettingsIdList: PredefinedSettingsIdList }> {
    const loggerObj = parentLoggerObj.createChildLogger("getProfiles");
    loggerObj.getLogger().debug("Entered Function");

    const res: Response = await fetch(`/api/v1/tns/${tn}/profiles`);
    if (!res.ok) {
        const err: string = await res.text();
        loggerObj.getLogger().debug(JSON.stringify(sanitizeLogMessage(err)));
        throw new Error(sanitizeErrorMessage(err), { cause: res.status });
    }
    const profiles: { predefinedSettingsIdList: PredefinedSettingsIdList } = await res.json();
    if (_.isEqual(profiles, { predefinedSettingsIdList: {} })) {
        await profilesFTUE(tn);
    }
    return profiles;
}

/**
 * This function is used to set the active profile for the given phone number.
 * @param tn - The phone number to set the active profile for.
 * @param profileId - The ID of the profile to set as active.
 */
export async function setActiveProfile(tn: string, profileId: string, parentLoggerObj: LoggerObject): Promise<void> {
    const loggerObj = parentLoggerObj.createChildLogger("setActiveProfile");
    loggerObj.getLogger().debug("Entered Function");

    const res: Response = await fetch(`/api/v1/tns/${tn}/profiles/${profileId}/status`, {
        method: "PUT",
    });
    if (!res.ok) {
        const err: string = await res.text();
        loggerObj.getLogger().debug(JSON.stringify(sanitizeLogMessage(err)));
        throw new Error(sanitizeErrorMessage(err), { cause: res.status });
    }
}

/**
 * This function is used to update the settings of a specific profile for the given phone number.
 * @param tn - The phone number whose profile is to be updated.
 * @param profileId - The ID of the profile to be updated.
 * @param newSettings - The new settings to be updated.
 */
export async function updateProfile(
    tn: string,
    profileId: string,
    newSettings: {
        predefinedSettingsProfile: PredefinedSettingsProfile;
    },
    parentLoggerObj: LoggerObject,
): Promise<void> {
    const loggerObj = parentLoggerObj.createChildLogger("updateProfile");
    loggerObj.getLogger().debug("Entered Function");

    const res: Response = await fetch(`/api/v1/tns/${tn}/profiles/${profileId}`, {
        method: "PATCH",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify(newSettings),
    });
    if (!res.ok) {
        const err: string = await res.text();
        throw new Error(sanitizeErrorMessage(err), { cause: res.status });
    }
}

/**
 * This function is used to delete a profile for the given phone number.
 * @param tn - The phone number to delete the profile for.
 * @param profileId - The ID of the profile to be deleted.
 */
export async function deleteProfile(tn: string, profileId: string, parentLoggerObj: LoggerObject): Promise<void> {
    const loggerObj = parentLoggerObj.createChildLogger("deleteProfile");
    loggerObj.getLogger().debug("Entered Function");

    const res: Response = await fetch(`/api/v1/tns/${tn}/profiles/${profileId}`, {
        method: "DELETE",
    });
    if (!res.ok) {
        const err: string = await res.text();
        loggerObj.getLogger().debug(JSON.stringify(sanitizeLogMessage(err)));
        throw new Error(sanitizeErrorMessage(err), { cause: res.status });
    }
}

/**
 * This function is used to create a new profile for the given phone number based off of the current comm settings.
 * @param tn - The phone number to create the profile for.
 * @param newProfile - The profile with voice settings to be created.
 */
export async function createProfileWithVoiceSettings(
    tn: string,
    newProfile: { predefinedSettingsProfile: PredefinedSettingsProfile },
    parentLoggerObj: LoggerObject,
): Promise<void> {
    const loggerObj = parentLoggerObj.createChildLogger("createProfile");
    loggerObj.getLogger().debug("Entered Function");

    const res: Response = await fetch(`/api/v1/tns/${tn}/profiles`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify(newProfile),
    });
    if (!res.ok) {
        const err: string = await res.text();
        // This outputs the toast overflow error message
        throw new Error(sanitizeErrorMessage(err), { cause: res.status });
    }
}

/**
 * This function is used to fetch the schedule for the given phone number.
 * @param tn - The phone number whose schedule is to be fetched.
 */
export async function getSchedule(tn: string): Promise<{ predefinedSettingsSchedule: PredefinedSettingsSchedule }> {
    const res: Response = await fetch(`/api/v1/tns/${tn}/schedule`);
    if (!res.ok) {
        const err: string = await res.text();
        throw new Error(sanitizeErrorMessage(err), { cause: res.status });
    }
    return res.json();
}

/**
 * This function is used to update the schedule for the given phone number.
 * @param tn - The phone number whose schedule is to be updated.
 * @param newSchedule - The new schedule to be updated.
 */
export async function updateSchedule(
    tn: string,
    newSchedule: {
        predefinedSettingsSchedule: PredefinedSettingsSchedule;
    },
): Promise<void> {
    const res: Response = await fetch(`/api/v1/tns/${tn}/schedule`, {
        method: "PUT",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify(newSchedule),
    });
    if (!res.ok) {
        const err: string = await res.text();
        throw new Error(sanitizeErrorMessage(err), { cause: res.status });
    }
}

/**
 * This function is used to set the schedule status for the given phone number.
 * @param tn - The phone number whose schedule status is to be set.
 * @param status - The status to be set.
 */
export async function setScheduleStatus(tn: string, status: boolean): Promise<void> {
    const payload: { settingsScheduleStatus: SettingsScheduleStatus } = {
        settingsScheduleStatus: {
            isActive: status,
        },
    };
    const res: Response = await fetch(`/api/v1/tns/${tn}/schedule/status`, {
        method: "PUT",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
    });
    if (!res.ok) {
        const err: string = await res.text();
        throw new Error(sanitizeErrorMessage(err), { cause: res.status });
    }
}
